import React from "react";
import { Alert } from 'react-bootstrap'
import { connect } from "react-redux";
import { IAppState, IOffline } from "../state/interfaces";
import QuoteStep from "./QuoteStepLayout";
import { IRate, IPaymentChoice, ICoverageConfig, ILOBApiClient, IQuoteApiClient, IPaths, IRealTimeAutoResult } from "../classes/Domain";
import ReactLoading from 'react-loading';
import { QUOTE_STEPS, APP_CONFIG } from "../AppConstants";
import RateResult from '../components/RateResult';
import Utilities from "../classes/Utilities";
import { IQuoteStepComponentProps, IQuoteStepState } from "../components/interfaces";
import QuoteStepBase from '../components/QuoteStepBase';
import ButtonContinue from "../components/ButtonContinue";
import QuoteActions from "../state/appActions";
import FullQuoteActions from "../state/fullQuoteActions";


interface IState extends IQuoteStepState  {
    offlineData: IOffline | null,
    rateResult: IRate | null,
    selectedRateResult: IRealTimeAutoResult | null | undefined,
    apiCallError: boolean,
    updatingStore: boolean,
    loadingRates: boolean,
    choiceSelected: boolean,
    issueChoice: boolean,
    paymentChoice: IPaymentChoice | null;
}

interface IResultComponentProps extends IQuoteStepComponentProps {
    quoteUid: string,
    coverageConfig: ICoverageConfig,
    fillExternalQuote: (update: any) => Promise<any>,
    fillOfflineData: () => Promise<any>,
    quoteApiClient: IQuoteApiClient,
    lobApiClient: ILOBApiClient,
    PATHS: IPaths;
}

class Result extends QuoteStepBase<IResultComponentProps, IState> {
    constructor(props: IResultComponentProps) {
        super(props);
        this.onPaymentChoiceSelected = this.onPaymentChoiceSelected.bind(this);
        this.issueNow = this.issueNow.bind(this);
        this.onApiCallError = this.onApiCallError.bind(this);

        this.state = {
            offlineData: null,
            rateResult: null,
            selectedRateResult: null,
            apiCallError:false,
            updatingStore:false,
            loadingRates: false,
            choiceSelected: false,
            issueChoice: false,
            paymentChoice: null
        };
    }

    componentDidMount() 
    {
        super.componentDidMount();
        this.props.reportCurrentStep();
        this.props.fillOfflineData().then((response) => {
            this.setState({ ...this.state, offlineData: response.data })
        }).then(() => this.getRates());
    }

    getRates() {
        const coveragesApiClient = this.props.quoteApiClient;

        this.setState({ loadingRates: true }, () => {
            coveragesApiClient
                .getRates(this.props.quoteUid, this.props.coverageConfig)
                .then((response) => {
                    const anyPaymentChoices = response.data.paymentChoices && 0 < response.data.paymentChoices.length;

                    if (!anyPaymentChoices) {
                        this.props.updateAppState({
                            quoteUid: this.props.quoteUid,
                            rate: null,
                            paymentChoice: null,
                            agenciesContactInfo: response.data.agenciesContactInfo,
                            rateError: response.data.isError ? response.data.msgStatusDesc : null
                        });

                        if (response.data.isError) {
                            this.props.history.goBack();
                        }
                        else {
                            this.props.history.replace(this.props.PATHS.AGENCIES);
                        }

                        return;
                    }

                    let _selectedRateResult = null;
                    let _selectedPaymentChoice: IPaymentChoice | null = null;

                    if (anyPaymentChoices) {
                        _selectedPaymentChoice = (response.data.paymentChoices as Array<IPaymentChoice>)
                            .sort((a, b) => (a.firstPay > b.firstPay) ? 1 : ((b.firstPay > a.firstPay) ? -1 : 0))
                            .sort((a, b) => (a.order > b.order) ? 1 : ((b.order > a.order) ? -1 : 0))[0];
                        _selectedPaymentChoice.selected = true;
                        _selectedRateResult = response.data.rates.find(x => x.program === _selectedPaymentChoice?.program)
                    }

                    this.setState({
                        ...this.state,
                        rateResult: {
                            rates: response.data.rates,
                            paymentChoices: response.data.paymentChoices,
                            agenciesContactInfo: response.data.agenciesContactInfo,
                            isError: response.data.isError,
                            errorCd: response.data.errorCd,
                            msgStatusCd: response.data.msgStatusCd,
                            msgStatusDesc: response.data.msgStatusDesc,
                            offerErrors: response.data.offerErrors
                        },
                        loadingRates: false,
                        paymentChoice: _selectedPaymentChoice,
                        selectedRateResult: _selectedRateResult,
                        choiceSelected: true
                    }, () => {
                            this.props.updateAppState({
                                quoteUid: this.props.quoteUid,
                                rate: this.state.selectedRateResult,
                                paymentChoice: this.state.paymentChoice,
                                agenciesContactInfo: this.state.rateResult?.agenciesContactInfo
                            })
                    });
                },
                () => {
                    this.setState({ ...this.state, loadingRates: false })
                });
        });
    }

    onApiCallError() {
        this.setState({ ...this.state, choiceSelected: false, issueChoice: false });
    }

    issueNow() {
        if (this.state.choiceSelected && null !== this.state.rateResult)
        {
            this.setState({ ...this.state, choiceSelected: true, issueChoice: true }, () => {
                if (null === this.state.rateResult) return;

                const selectedRate = {
                    ...this.state.selectedRateResult, urlMemo: this.state.paymentChoice?.urlMemo ?? "", quoteUid: this.props.quoteUid
                };

                var lobApiClient = this.props.lobApiClient;

                lobApiClient.createtQuote(selectedRate)
                    .then((response) => {
                        const quoteId = parseInt(response.data, 10);

                        lobApiClient.getQuote(quoteId)
                            .then((qResponse) => {
                                qResponse.data.rtrQuoteId = quoteId;
                                qResponse.data.drivers.forEach(x => x.birthDate = '')
                                this.props.fillExternalQuote({ quote: qResponse.data })
                                    .then(() => this.props.history.replace(Utilities.formatUrl(this.props.PATHS.FQ_YOUR_HOME, "")));
                            });
                    }, this.onApiCallError)
            });
        }
    }

    onPaymentChoiceSelected(paymentChoice: IPaymentChoice) {
        this.state.rateResult?.paymentChoices.filter(x => x.urlMemo !== paymentChoice.urlMemo).forEach(x => x.selected = false);

        this.setState({
            rateResult: this.state.rateResult,
            selectedRateResult: this.state.rateResult?.rates.find(x => x.program === (paymentChoice.selected ? paymentChoice.program : "")),
            paymentChoice: paymentChoice.selected ? paymentChoice : null,
            choiceSelected: paymentChoice.selected
        }, () => {
                this.props.updateAppState({
                    quoteUid: this.props.quoteUid,
                    rate: this.state.selectedRateResult,
                    paymentChoice: this.state.paymentChoice,
                    agenciesContactInfo: this.state.rateResult?.agenciesContactInfo
                })
        })
    }

    render() {
        return (
            <QuoteStep id={this.props.id}>
                <div className="quote-step rates">
                    {this.state.offlineData && this.state.offlineData.isActive && !this.state.loadingRates
                        ?
                        <div>
                            <Alert variant={"warning"}>
                                <p><strong className="text-uppercase">Sorry! Can not process your quote!</strong></p>
                                <p>Please review information below</p>
                                <ul>
                                    <p className="message-body" dangerouslySetInnerHTML={{ __html: this.state.offlineData.message }}></p>
                                </ul>
                            </Alert>
                        </div>
                        :
                        null
                    }

                    {this.state.loadingRates
                        ? <div className={"w-100 loading-indicator"}><ReactLoading type={"spin"} color={"#34B629"} height={'40px'} width={'40px'} /></div>
                        : null
                    }

                    {!this.state.loadingRates && null !== this.state.rateResult
                        ?
                        <div>
                            {[
                                this.state.rateResult 
                            ].map((rate, idx) => (
                                <RateResult
                                    key={Utilities.uniqueId()}
                                    rateResult={rate}
                                    currentRate={this.state.selectedRateResult}
                                    index={idx}
                                    onPaymentChoiceSelected={this.onPaymentChoiceSelected}
                                    reRate={() => this.getRates()} />
                            ))}
                        </div>
                        : null
                    }
                </div>
                <br />
                <div className="choice">
                    {this.state.offlineData && this.state.offlineData.isActive
                        ?
                        null
                        :
                        <div className="text-center">
                            <ButtonContinue
                                disabled={(this.state.offlineData && this.state.offlineData.isActive) || this.state.loadingRates || this.state.rateResult?.paymentChoices.every(x => !x.selected)}
                                showLoading={this.state.choiceSelected && this.state.issueChoice}
                                label="Buy Now"
                                onClick={this.issueNow} />
                        </div>
                    }
                </div>
            </QuoteStep>
        );
    }
}

let mapProps = (state: IAppState) => {
    return {
        quoteUid: state.quoteUid,
        coverageConfig: state.coverageConfig,
        stateCode: state.stateCode
    };
};

let mapDispatch = (dispatch: any, ownProps: IResultComponentProps) => {
    var quoteActions = new QuoteActions(ownProps.quoteApiClient);
    var fullQuoteActions = new FullQuoteActions(ownProps.lobApiClient);

    return {
        reportCurrentStep: () => dispatch(quoteActions.quoteStepChanged(QUOTE_STEPS.COVERAGES)),
        updateAppState: (update) => dispatch(quoteActions.selectPaymentChoice(update.quoteUid, update.rate, update.paymentChoice, update.agenciesContactInfo, update.rateError)),
        fillExternalQuote: (update) => dispatch(fullQuoteActions.fillExternalQuote(update.quote)),
        fillOfflineData: () => dispatch(quoteActions.fillOfflineData())
    };
};

export default connect(mapProps, mapDispatch)(Result);
